"use client"

import { useEffect } from "react"
import {
  clearViewContextSnapshot,
  readPageSnapshot,
  setViewContextSnapshot,
} from "./store"
import type { ViewContextEnvelope } from "./types"

/**
 * Page hook: publish the current view to the global snapshot store.
 *
 * Pass `null` (or omit the call) when the page has no meaningful view to
 * publish. Unmount clears whatever the page set, so navigating to a page
 * that doesn't publish leaves the AI with no view context — better than
 * stale.
 *
 * Cost: this writes to a module-level ref on every render where
 * `snapshot` changes by reference. Wrap the snapshot in `useMemo` on the
 * caller side so it only rebuilds when the underlying state actually
 * changes; the chat panel reads at send time, not on every keystroke.
 *
 * Unmount only clears the snapshot if no later page has already
 * overwritten it — protects against flicker during Next.js layout
 * transitions where one page unmounts after another mounts.
 */
export function useSetViewContext(snapshot: ViewContextEnvelope | null): void {
  useEffect(() => {
    setViewContextSnapshot(snapshot)
    return () => {
      // Compare the raw PAGE snapshot (not the merged read, which returns a
      // fresh object when a focused email / note-reply is active) so this
      // page's context is reliably cleared on unmount.
      if (readPageSnapshot() === snapshot) {
        clearViewContextSnapshot()
      }
    }
  }, [snapshot])
}
